import { useQuery } from '@tanstack/react-query';
import { useSettings } from './useSettings';
import { authenticatedFetch } from '@/lib/api';

interface Company {
  Id: number;
  Name: string;
  State?: string;
  CompanyID?: number;
  [key: string]: any;
}

interface CompaniesApiResponse {
  data?: Company[];
  status?: number;
  message?: string;
}

/**
 * Resolves the Primary Company (from Settings) and returns its State from api/Companies.
 * Used for GST same-state vs inter-state logic (e.g. SGST/CGST vs IGST).
 */
export function useCompanyState() {
  const { getSetting } = useSettings();

  const primaryCompanyId = getSetting('Primary Company ID')?.trim() || '';

  const { data: companiesResponse, isLoading } = useQuery({
    queryKey: ['companies-for-state', primaryCompanyId],
    queryFn: async () => {
      const res = await authenticatedFetch<CompaniesApiResponse>('/api/Companies');
      return res;
    },
    staleTime: 1000 * 60 * 5,
    enabled: true,
  });

  const raw = (companiesResponse as any)?.data;
  const companies: Company[] = Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw)
    ? raw
    : [];

  const company = primaryCompanyId
    ? companies.find(
        (c) =>
          String(c.Id) === primaryCompanyId ||
          String(c.CompanyID) === primaryCompanyId
      )
    : undefined;

  const companyState = (company?.State ?? '').trim();

  return {
    companyState,
    company,
    primaryCompanyId: primaryCompanyId || undefined,
    isLoading,
  };
}
